import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { BisacCode } from "reedy-data/api";
import { BisacCodesBrowser } from "components/BisacCodes/BisacCodesBrowser";
import { Button } from "components/button";
import { useAnalytics } from "contexts/AnalyticsContext";
import { toast } from "utils/toast";
import { useCodesQueryParam } from "./useCodesQueryParam";

interface BisacCodesContentProps {
  kindToggle?: ReactNode;
}

export const BisacCodesContent = ({ kindToggle }: BisacCodesContentProps) => {
  const { t } = useTranslation("bisacCodes");
  const { gaEvent } = useAnalytics();
  const { submittedQuery, setSubmittedQuery } = useCodesQueryParam("BISAC");

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      gaEvent({
        type: "copy_bisac_code",
        payload: { code, query: submittedQuery },
      });
      toast.success(t("bisacCodes:copy.success"));
    } catch {
      toast.error(t("bisacCodes:copy.failed"));
    }
  };

  const renderCopyAction = ({ code }: BisacCode) => (
    <Button
      variant="ghost"
      size="small"
      onClick={() => copyCode(code)}
      className="size-8 p-0 hover:text-primary-800 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
      aria-label={t("bisacCodes:copy.label", { code })}
      icon="copy"
    />
  );

  return (
    <BisacCodesBrowser
      submittedQuery={submittedQuery}
      onSubmittedQueryChange={setSubmittedQuery}
      renderRowAction={renderCopyAction}
      searchBarEnd={kindToggle}
    />
  );
};
